import { useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  type ChartOptions
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import type { Stats as StatsType, Todo } from "../types";
import { todayKey } from "../utils/storage";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

interface StatsProps {
  stats: StatsType;
  todos: Todo[];
}

function formatMinutes(min: number): string {
  if (min < 60) return `${min} 分钟`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h} 小时` : `${h} 小时 ${m} 分钟`;
}

function shortWeekday(d: Date): string {
  return ["日", "一", "二", "三", "四", "五", "六"][d.getDay()];
}

function dateLabel(d: Date): string {
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

const COLOR = {
  focus: "#c44d4d",
  focusSoft: "rgba(196, 77, 77, 0.9)",
  short: "#3d8b76",
  long: "#5b7c99",
  amber: "#d9923f",
  amberSoft: "rgba(217, 146, 63, 0.55)",
  body: "#4d4d51",
  muted: "#7a7a80",
  soft: "#a8a8ae",
  line: "#e8e4dd"
};

export default function Stats({ stats, todos }: StatsProps) {
  const last7 = useMemo(() => {
    const arr: { key: string; label: string; weekday: string; pomodoros: number; minutes: number }[] = [];
    const today = new Date();
    for (let i = 6; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const key = todayKey(d);
      const item = stats.byDate[key] ?? { pomodoros: 0, minutes: 0 };
      arr.push({
        key,
        label: dateLabel(d),
        weekday: shortWeekday(d),
        pomodoros: item.pomodoros,
        minutes: item.minutes
      });
    }
    return arr;
  }, [stats]);

  const todayKeyStr = todayKey();
  const today = stats.byDate[todayKeyStr] ?? { pomodoros: 0, minutes: 0 };
  const weekPomodoros = last7.reduce((s, d) => s + d.pomodoros, 0);
  const weekMinutes = last7.reduce((s, d) => s + d.minutes, 0);

  const topTasks = useMemo(() => {
    return [...todos]
      .filter((t) => t.completedPomodoros > 0)
      .sort((a, b) => b.completedPomodoros - a.completedPomodoros)
      .slice(0, 5);
  }, [todos]);
  const maxTaskPomo = topTasks[0]?.completedPomodoros ?? 1;

  // Chart.js 配置
  const barData = {
    labels: last7.map((d) => `${d.weekday} ${d.label}`),
    datasets: [
      {
        label: "番茄数",
        data: last7.map((d) => d.pomodoros),
        backgroundColor: last7.map((d) =>
          d.key === todayKeyStr ? COLOR.focusSoft : COLOR.amberSoft
        ),
        borderColor: last7.map((d) => (d.key === todayKeyStr ? COLOR.focus : COLOR.amber)),
        borderWidth: 1,
        borderRadius: 6,
        hoverBackgroundColor: COLOR.focus
      }
    ]
  };

  const barOptions: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#2d2d2f",
        titleColor: "#ffffff",
        bodyColor: "#ffffff",
        padding: 12,
        cornerRadius: 8,
        displayColors: false,
        titleFont: { size: 12, weight: 600 },
        bodyFont: { size: 12 },
        callbacks: {
          label: (ctx) => {
            const idx = ctx.dataIndex;
            const d = last7[idx];
            return [`${d.pomodoros} 个番茄`, `${d.minutes} 分钟`];
          }
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: { color: COLOR.muted, font: { size: 11 } }
      },
      y: {
        beginAtZero: true,
        grid: { color: COLOR.line },
        border: { display: false },
        ticks: {
          color: COLOR.muted,
          font: { size: 11 },
          stepSize: 1,
          precision: 0
        }
      }
    }
  };

  const doughnutData = {
    labels: ["今日完成", "本周其他"],
    datasets: [
      {
        data: [today.pomodoros, Math.max(0, weekPomodoros - today.pomodoros)],
        backgroundColor: [COLOR.focus, COLOR.amberSoft],
        borderColor: "#fcfaf6",
        borderWidth: 3,
        hoverOffset: 6
      }
    ]
  };

  const doughnutOptions: ChartOptions<"doughnut"> = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "70%",
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          color: COLOR.body,
          font: { size: 12 },
          padding: 12,
          boxWidth: 10,
          boxHeight: 10,
          usePointStyle: true
        }
      },
      tooltip: {
        backgroundColor: "#2d2d2f",
        titleColor: "#ffffff",
        bodyColor: "#ffffff",
        padding: 12,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: (ctx) => `${ctx.label}：${ctx.parsed} 个`
        }
      }
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* 指标卡 */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <TodayCard value={today.pomodoros} minutes={today.minutes} />
        <MiniCard
          label="本周完成"
          value={weekPomodoros}
          unit="个"
          sub={`合计 ${formatMinutes(weekMinutes)}`}
          accent="text-amber"
          dot="bg-amber"
          bg="bg-amber-soft"
        />
        <MiniCard
          label="累计完成"
          value={stats.totalPomodoros}
          unit="个"
          sub={`合计 ${formatMinutes(stats.totalMinutes)}`}
          accent="text-long"
          dot="bg-long"
          bg="bg-long-tint"
        />
        <MiniCard
          label="任务覆盖"
          value={topTasks.length}
          unit="项"
          sub={topTasks.length > 0 ? `最多 ${maxTaskPomo} 个` : "暂无记录"}
          accent="text-short"
          dot="bg-short"
          bg="bg-short-tint"
        />
      </div>

      {/* 双图卡 */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="sm:col-span-2 bg-paper border border-hairline rounded-2xl shadow-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-ink flex items-center gap-2">
              <span className="w-1.5 h-4 bg-focus rounded-full" />
              最近 7 天趋势
            </h3>
            <span className="text-xs text-soft">单位：个</span>
          </div>
          <div className="h-56">
            <Bar data={barData} options={barOptions} />
          </div>
        </div>

        <div className="bg-paper border border-hairline rounded-2xl shadow-card p-5 flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-ink flex items-center gap-2">
              <span className="w-1.5 h-4 bg-amber rounded-full" />
              本周今日占比
            </h3>
          </div>
          <div className="flex-1 min-h-[180px] relative">
            <Doughnut data={doughnutData} options={doughnutOptions} />
            {weekPomodoros === 0 && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="text-xs text-soft">暂无数据</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 任务排行 */}
      <div className="bg-paper border border-hairline rounded-2xl shadow-card p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-ink flex items-center gap-2">
            <span className="w-1.5 h-4 bg-short rounded-full" />
            任务番茄数排行
          </h3>
          <span className="text-xs text-soft">Top 5</span>
        </div>
        {topTasks.length === 0 ? (
          <div className="text-center text-sm text-soft py-8 bg-chip rounded-lg border border-hairline">
            还没有任务完成记录，开始一个番茄试试
          </div>
        ) : (
          <ul className="flex flex-col gap-2.5">
            {topTasks.map((t, idx) => {
              const pct = (t.completedPomodoros / maxTaskPomo) * 100;
              return (
                <li key={t.id} className="flex items-center gap-3">
                  <span
                    className={`flex-none w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold ${
                      idx === 0
                        ? "bg-focus-soft text-focus"
                        : idx === 1
                        ? "bg-chip2 text-body"
                        : idx === 2
                        ? "bg-amber-soft text-amber"
                        : "bg-chip text-soft"
                    }`}
                  >
                    {idx + 1}
                  </span>
                  <span
                    className={`flex-1 truncate text-sm ${
                      t.completed ? "line-through text-soft" : "text-ink"
                    }`}
                  >
                    {t.title}
                  </span>
                  <div className="flex-none w-32 sm:w-48 h-2 bg-chip2 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-focus rounded-full"
                      style={{
                        width: `${pct}%`,
                        transition: "width 0.6s cubic-bezier(0.4, 0, 0.2, 1)"
                      }}
                    />
                  </div>
                  <span className="flex-none w-10 text-right text-sm font-numeric text-focus font-semibold">
                    {t.completedPomodoros}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* 激励语 */}
      <div className="text-center text-sm text-body">
        {today.pomodoros === 0 && (
          <span className="text-soft">今天还未开始 · 开启第一个番茄吧</span>
        )}
        {today.pomodoros >= 1 && today.pomodoros < 4 && "不错的开始，继续加油"}
        {today.pomodoros >= 4 && today.pomodoros < 8 && "已进入状态，保持节奏"}
        {today.pomodoros >= 8 && "今日高效，给自己一个掌声"}
      </div>
    </div>
  );
}

interface TodayCardProps {
  value: number;
  minutes: number;
}

function TodayCard({ value, minutes }: TodayCardProps) {
  const GOAL = 8;
  const pct = Math.min(100, (value / GOAL) * 100);
  return (
    <div className="bg-focus text-white rounded-2xl p-4 relative overflow-hidden shadow-card">
      <div className="absolute -right-5 -top-5 w-24 h-24 bg-white/10 rounded-full" />
      <div className="absolute -right-10 top-8 w-20 h-20 bg-white/5 rounded-full" />
      <div className="relative">
        <div className="flex items-center gap-1.5 text-xs opacity-90">
          <span className="w-1 h-1 rounded-full bg-white" />
          <span>今日番茄</span>
        </div>
        <div className="flex items-baseline gap-1 mt-2 font-numeric">
          <span className="text-4xl font-semibold leading-none">{value}</span>
          <span className="text-sm opacity-70">/ {GOAL}</span>
        </div>
        <div className="mt-3 h-1 bg-white/25 rounded-full overflow-hidden">
          <div
            className="h-full bg-white rounded-full"
            style={{
              width: `${pct}%`,
              transition: "width 0.6s cubic-bezier(0.4, 0, 0.2, 1)"
            }}
          />
        </div>
        <div className="mt-2 text-xs opacity-90">专注 {formatMinutes(minutes)}</div>
      </div>
    </div>
  );
}

interface MiniCardProps {
  label: string;
  value: number;
  unit: string;
  sub: string;
  accent: string;
  dot: string;
  bg: string;
}

function MiniCard({ label, value, unit, sub, accent, dot, bg }: MiniCardProps) {
  return (
    <div className={`relative ${bg} border border-hairline rounded-2xl p-4 overflow-hidden`}>
      <div className="flex items-center gap-1.5">
        <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
        <span className="text-xs text-body">{label}</span>
      </div>
      <div className="flex items-baseline gap-1 mt-2 font-numeric">
        <span className={`text-3xl font-semibold ${accent}`}>{value}</span>
        <span className="text-xs text-soft">{unit}</span>
      </div>
      <div className="text-xs text-muted mt-1">{sub}</div>
    </div>
  );
}
